import React, { useEffect, useMemo, useState } from "react";
import Submenu from "../../components/submenu/Submenu";
import { getRouteParams, routes } from "../../app/routes";
import { useLocation } from "react-router-dom";
import InspectionDetails from "../../data/entities/InspectionDetailsModel";
import InspectionService from "../../services/InspectionService";
import "../../css/bootstrap.scss";
import { ExploreCard } from "./ExploreCard";
import { FLWCard } from "./FLWCard";
import { setActiveInspectionId } from "../../app/store/inspectionSlice";
import InspectorList from "../../components/inspections/InspectorList";
import InspectionSummary from "./InspectionSummary";
import InspectionCriteria from "./InspectionCriteria";
import InspectionNotes from "./InspectionNotes";
import {
  SimpleCard,
  SpinnerDefault,
  SubmenuNavigationItem,
  TitlelessCard,
} from "../../components";
import { store } from "../../app/store/store";
import PageContent from "../PageContent";
import { Alert } from "react-bootstrap";
import { GaugeConfig, PeakSignalModel, PeakSignalUpdateModel, ScaleModel } from "../../data/entities";
import { PeakSignalTable } from "../cteye/components";
import { UserService } from "../../services";
import { Roles } from "../../models";

export function MyInspectionDetailsPage() {
  const { pathname } = useLocation();

  const [pageData, setPageData] = useState<
    InspectionDetails | undefined | null
  >(undefined);
  const [hasOvalityData, setHasOvalityData] = useState(false);
  const [inspectionScale, setInspectionScale] = useState<ScaleModel>();
  const [peakSignals, setPeakSignals] = useState<PeakSignalModel[]>([]);
  const [peakGaugeConfig, setPeakGaugeConfig] = useState<GaugeConfig | null>(null);
  const id = Number.parseInt(
    getRouteParams(routes.myInspectionsDetails, pathname).get("id") ?? "0"
  );
  const params = useMemo(
    () => new Map<string, string>([["id", id.toString()]]),
    [id]
  );
  const buildMenu = useMemo(
    () => [
      { label: "Back", url: routes.myInspections, icon: "arrow-left" },
      {
        label: "Details",
        url: routes.myInspectionsDetails,
        params: new Map<string, string>([["id", id.toString()]]),
        activeMatch: routes.myInspectionsDetails,
      },
    ],
    [id]
  );
  const [submenus, setSubmenus] = useState<SubmenuNavigationItem[]>(buildMenu);

  useEffect(() => {
    const fetchData = async () => {
      const loadedData = await new InspectionService().getInspectionDetails(
        id.toString()
      );

      setPageData(loadedData);
      setHasOvalityData(loadedData?.hasOvalityData ?? false);
    };
    const fetchScale = async () => {
      const scale = await new InspectionService().getScale(id);
      if (scale) setInspectionScale(scale);
    }
    const fetchPeakSignals = async () => {
      const response = await new InspectionService().getPeakSignals(id);
      setPeakSignals(response?.peakSignals ?? []);
      setPeakGaugeConfig(response?.gaugeSettings ?? null);
    }
    setPageData(undefined);
    setPeakSignals([]);
    setPeakGaugeConfig(null);
    store.dispatch(setActiveInspectionId(undefined));
    fetchData().catch((error) => {});
    fetchScale().catch((error) => {});
    fetchPeakSignals().catch((error) => {});

    return () => {};
  }, [id]);

  useEffect(() => {
    if (hasOvalityData) {
      setSubmenus([
        ...buildMenu,
        {
          label: "Ovality",
          url: routes.myInspectionsOvality,
          params,
          activeMatch: routes.myInspectionsOvality,
        },
        {
          label: "OD",
          url: routes.myInspectionsOD,
          params,
          activeMatch: routes.myInspectionsOD,
        },
      ]);
    }
    return () => {};
  }, [hasOvalityData, params, buildMenu]);

  const reloadPeakSignals = async () => {
    const response = await new InspectionService().getPeakSignals(id);
    setPeakSignals(response?.peakSignals ?? []);
    setPeakGaugeConfig(response?.gaugeSettings ?? null);
  };

  const canEditPeaks = UserService.isInAnyRole([Roles.admin, Roles.dataAnalyst]);
  const handlePeakUpdate = async (signal: PeakSignalModel, changes: PeakSignalUpdateModel) => {
    await new InspectionService().updatePeakSignal(signal.id, changes);
    await reloadPeakSignals();
  };
  const handlePeakDelete = async (signal: PeakSignalModel) => {
    await new InspectionService().deletePeakSignal(signal.id);
    await reloadPeakSignals();
  };

  const alertMessage = pageData?.uiMessage ? (
    <Alert variant="warning" dismissible={false}>
      <p className="mb-0">{pageData?.uiMessage}</p>
    </Alert>
  ) : (
    <></>
  );

  let content: React.JSX.Element;
  if (pageData === undefined) content = <SpinnerDefault />;
  else if (pageData === null)
    content = (
      <SimpleCard title="Failed to load data.">
        <div>
          <em>No inspection data to show.</em>
        </div>
      </SimpleCard>
    );
  else {
    const lfPeakSignals = peakSignals.filter((p) => p.signalType === "LF");
    const lmaPeakSignals = peakSignals.filter((p) => p.signalType === "LMA");

    content = (
      <>
        {alertMessage}
        <ExploreCard
          chartData={pageData.chart}
          tubing={pageData.tubing!}
          reference={pageData.reference!}
          inspectionDate={pageData.inspectionDate}
          overallThreshold={pageData.overallThreshold ?? 0}
        />
        {lfPeakSignals.length > 0 && (
          <TitlelessCard className="p-3 mb-3">
            <PeakSignalTable
              peakSignals={lfPeakSignals}
              config={peakGaugeConfig}
              canEdit={canEditPeaks}
              onUpdate={handlePeakUpdate}
              onDelete={handlePeakDelete}
            />
          </TitlelessCard>
        )}
        <InspectionCriteria pageData={pageData} />
        <InspectionSummary pageData={pageData} />
        <FLWCard
          chartData={pageData.flwValues}
          scale={inspectionScale}
          reference={pageData.reference!}
          tubing={pageData.tubing!}
          flwLow={pageData.flwLow}
          flwHigh={pageData.flwHigh}
          notes={pageData.notes}
        />
        {lmaPeakSignals.length > 0 && (
          <TitlelessCard className="p-3 mb-3">
            <PeakSignalTable
              peakSignals={lmaPeakSignals}
              config={peakGaugeConfig}
              canEdit={canEditPeaks}
              onUpdate={handlePeakUpdate}
              onDelete={handlePeakDelete}
            />
          </TitlelessCard>
        )}
        <InspectorList inspectors={pageData?.inspectors} />
        <InspectionNotes pageData={pageData} />
      </>
    );
  }

  return (
    <PageContent submenu={<Submenu items={submenus}></Submenu>}>
      {content}
    </PageContent>
  );
}

export default MyInspectionDetailsPage;

